import React, { FC, useEffect, useState } from 'react';
import Contact, { EditContactOptions } from '../../../../interfaces/api/Contact';
import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  LinearProgress,
  OutlinedInput,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { createUseStyles } from '../../../../theming';
import { Controller, useForm } from 'react-hook-form';
import Button from '../../../../components/Button';
import Card from 'src/components/Card';
import useAuth from '../../../../hooks/api/useAuth';
import { useNotifier } from '../../../../components/NotificationCenter/AlertNotificationCenter';

interface Props {
  enableContact: () => void;
  disableContact: () => void;
  updateContact: (contact: EditContactOptions) => Promise<void>;
  history: any;
  contact?: Contact;
  loading?: boolean;
  error?: string;
}

const useStyles = createUseStyles({
  root: {
    padding: 24,
  },
  item: {
    marginTop: '8px !important',
    marginBottom: '8px !important',
  },
  actions: {
    marginTop: 8,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  error: {
    color: '#f44336',
    fontSize: '.75rem',
    padding: '0 25px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px !important',
  },
  chip: {
    height: 22,
    minWidth: 22,
    lineHeight: 0,
    borderRadius: 6,
    alignItems: 'center',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    justifyContent: 'center',
    padding: '0px 8px',
    color: 'rgb(183, 33, 54)',
    fontSize: '0.75rem',
    backgroundColor: 'rgba(255, 72, 66, 0.16)',
    fontWeight: 700,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
});

const View: FC<Props> = ({ disableContact, enableContact, updateContact, contact, loading, history }) => {
  const classes = useStyles();
  const { addNotification } = useNotifier();

  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
    reset,
  } = useForm<EditContactOptions>();

  useEffect(() => {
    if (contact) {
      reset({
        job: contact?.job,
        email: contact?.email,
        firstname: contact?.firstname,
        lastname: contact?.lastname,
        phoneNumber: contact?.phoneNumber,
      });
    }
  }, [contact]);

  const onSubmit = async (contactOptions: EditContactOptions) => {
    try {
      await updateContact(contactOptions);
      addNotification({
        title: 'Contact mis à jour avec succès !',
        alertProps: { color: 'success' },
      });
    } catch (e) {
      addNotification({
        title: "Echec lors de l'enregistrement du contact ...",
        alertProps: { color: 'danger' },
      });
    }
  };

  return (
    <Grid className={classes.root} justifyContent='center' container>
      <Grid item md={9} xs={12}>
        <Card>
          <div className={classes.header}>
            <Typography variant='h6'>Information du contact</Typography>

            {contact &&
              (!contact.enabled ? (
                <div className={classes.chip}>Désactivé</div>
              ) : (
                <Button variant='text' color='secondary' onClick={disableContact}>
                  Désactiver le contact
                </Button>
              ))}
          </div>

          {contact && !contact.enabled && (
            <Alert severity='info' style={{ marginBottom: 16 }}>
              Vous devez ré-activer le contact pour pouvoir modifier les données
            </Alert>
          )}

          {contact && contact.legacyContactId && (
            <Alert
              severity='warning'
              style={{ marginBottom: 16 }}
              action={
                <Button
                  color='inherit'
                  size='small'
                  onClick={() => history.push(`/dashboard/contact/${contact.legacyContactId}`)}
                >
                  Accèder a la fiche
                </Button>
              }
            >
              Ce contact a été migré de l'ancien système.
              <LegacyContact legacyContactId={contact.legacyContactId} />
            </Alert>
          )}

          {!loading && (
            <>
              <Grid container spacing={2}>
                <Grid item md={6}>
                  <Controller
                    name='firstname'
                    control={control}
                    rules={{ required: { value: true, message: 'Le champ est requis' } }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl
                        error={error !== undefined}
                        fullWidth
                        className={classes.item}
                        disabled={!contact?.enabled}
                        variant='outlined'
                      >
                        <InputLabel htmlFor={field.name}>Prénom</InputLabel>
                        <OutlinedInput {...field} label='Prénom' id={field.name} labelWidth={60} />
                        {error && <FormHelperText>{error.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item md={6}>
                  <Controller
                    name='lastname'
                    control={control}
                    rules={{ required: { value: true, message: 'Le champ est requis' } }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl
                        error={error !== undefined}
                        fullWidth
                        className={classes.item}
                        disabled={!contact?.enabled}
                        variant='outlined'
                      >
                        <InputLabel htmlFor={field.name}>Nom</InputLabel>
                        <OutlinedInput {...field} label='Nom' id={field.name} labelWidth={60} />
                        {error && <FormHelperText>{error.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>

              <Controller
                name='email'
                control={control}
                rules={{ required: { value: true, message: 'Le champ est requis' } }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    error={error !== undefined}
                    fullWidth
                    className={classes.item}
                    disabled={!contact?.enabled}
                    variant='outlined'
                  >
                    <InputLabel htmlFor={field.name}>Email</InputLabel>
                    <OutlinedInput {...field} label='Email' id={field.name} labelWidth={60} />
                    {error && <FormHelperText>{error.message}</FormHelperText>}
                  </FormControl>
                )}
              />

              <Controller
                name='phoneNumber'
                control={control}
                rules={{ required: { value: true, message: 'Le champ est requis' } }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    error={error !== undefined}
                    fullWidth
                    className={classes.item}
                    disabled={!contact?.enabled}
                    variant='outlined'
                  >
                    <InputLabel htmlFor={field.name}>Téléphone</InputLabel>
                    <OutlinedInput {...field} label='Téléphone' id={field.name} labelWidth={60} />
                    {error && <FormHelperText>{error.message}</FormHelperText>}
                  </FormControl>
                )}
              />

              <Controller
                name='job'
                control={control}
                rules={{ required: { value: true, message: 'Le champ est requis' } }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    error={error !== undefined}
                    fullWidth
                    className={classes.item}
                    disabled={!contact?.enabled}
                    variant='outlined'
                  >
                    <InputLabel htmlFor={field.name}>Job</InputLabel>
                    <OutlinedInput {...field} label='Job' id={field.name} labelWidth={60} />
                    {error && <FormHelperText>{error.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </>
          )}

          <div className={classes.actions}>
            {contact?.enabled ? (
              <Button
                color='primary'
                variant='contained'
                onClick={handleSubmit(onSubmit)}
                disabled={!isDirty || isSubmitting}
              >
                {isSubmitting ? 'Enregistrement en cours...' : 'Enregistrer'}
              </Button>
            ) : (
              contact && (
                <Button variant='text' color='primary' onClick={enableContact}>
                  Ré-activer le contact
                </Button>
              )
            )}
          </div>

          {(loading || isSubmitting) && <LinearProgress />}
        </Card>
      </Grid>
    </Grid>
  );
};

const LegacyContact: FC<{ legacyContactId: number }> = ({ legacyContactId }) => {
  const [contact, setContact] = useState<{
    firstname?: string;
    lastname?: string;
    phone?: string;
    email?: string;
    job?: string;
    companyName?: string;
  }>();

  const { fetch } = useAuth();

  useEffect(() => {
    let queryURL = '/api/contact/show/';
    let graphQL =
      '{ contact(id:' +
      legacyContactId +
      '){ id, name, lastname, phoneNumber, email, job, companyName, deleted, origin{ id, labelOrigin }, ' +
      'creator{ user{ id, username } }, ' +
      'customer{ id, installationSite{ socialReason }}, ' +
      'otherCustomersAssociated{ id, installationSite{ socialReason }, dateCreate, creator{ user{ id, username }}}}}';
    queryURL += graphQL;

    fetch(queryURL, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (data['data']['contact']) {
          let checkedJob = data['data']['contact']['job'] ? data['data']['contact']['job'] : '';
          let checkedCompanyName = data['data']['contact']['companyName'] ? data['data']['contact']['companyName'] : '';

          setContact({
            firstname: data['data']['contact']['name'],
            lastname: data['data']['contact']['lastname'].toUpperCase(),
            phone: data['data']['contact']['phoneNumber'],
            email: data['data']['contact']['email'],
            job: checkedJob,
            companyName: checkedCompanyName,
          });
        }
      });
  }, [legacyContactId]);

  if (!contact) {
    return (
      <div>
        <em>Chargement ...</em>
      </div>
    );
  }

  return (
    <>
      <div>
        {contact.firstname} {contact.lastname}
      </div>
      <div>
        <strong>Téléphone:</strong> {contact.phone}
      </div>
      <div>
        <strong>Email:</strong> {contact.email}
      </div>
    </>
  );
};

export default View;
